export const MINIMAP_PREFERENCES_STORAGE_KEY = "vilnius.minimap.v1";

export interface MinimapPreferences {
  readonly schemaVersion: 1;
  readonly visible: boolean;
}

export interface MinimapPalette {
  readonly ground: string;
  readonly vegetation: string;
  readonly water: string;
  readonly roadCasing: string;
  readonly road: string;
  readonly railwayCasing: string;
  readonly railway: string;
  readonly path: string;
  readonly building: string;
  readonly buildingEdge: string;
  readonly player: string;
  readonly playerOutline: string;
  readonly viewCone: string;
  readonly waypoint: string;
  readonly waypointOutline: string;
}

/**
 * Provisional, user-facing local-map presentation values.
 *
 * The map deliberately stays at one stable walking scale. These values need a
 * hands-on user tuning pass before they should be considered final.
 */
export const MINIMAP_CONFIG = Object.freeze({
  sizeCssPx: 192,
  radiusM: 120,
  overscanM: 32,
  rebuildDistanceM: 16,
  lineSimplificationToleranceM: 0.75,
  maxDevicePixelRatio: 2,
  minimumRoadStrokeCssPx: 2.2,
  maximumRoadStrokeCssPx: 6.2,
  roadCasingExtraCssPx: 1.35,
  railwayStrokeCssPx: 1.25,
  railwayCasingExtraCssPx: 1.6,
  pathStrokeCssPx: 1.15,
  waterStrokeCssPx: 4.5,
  buildingStrokeCssPx: 0.65,
  playerMarkerLengthCssPx: 10,
  playerMarkerWidthCssPx: 7,
  viewDirectionLengthCssPx: 24,
  viewDirectionHalfAngleRadians: Math.PI / 9,
  waypointMarkerRadiusCssPx: 5.5,
  waypointEdgeInsetCssPx: 13,
  palettes: Object.freeze({
    light: Object.freeze({
      ground: "oklch(88% 0.024 88)",
      vegetation: "oklch(72% 0.035 142)",
      water: "oklch(58% 0.048 186)",
      roadCasing: "oklch(30% 0.019 145)",
      road: "oklch(82% 0.018 88)",
      railwayCasing: "oklch(30% 0.012 75)",
      railway: "oklch(70% 0.014 75)",
      path: "oklch(42% 0.022 145)",
      building: "oklch(67% 0.039 68)",
      buildingEdge: "oklch(34% 0.025 145)",
      player: "oklch(62% 0.095 53)",
      playerOutline: "oklch(20% 0.022 145)",
      viewCone: "oklch(48% 0.06 55 / 22%)",
      waypoint: "oklch(65% 0.13 63)",
      waypointOutline: "oklch(20% 0.025 145)",
    } satisfies MinimapPalette),
    dark: Object.freeze({
      ground: "oklch(22% 0.018 145)",
      vegetation: "oklch(35% 0.035 142)",
      water: "oklch(48% 0.052 186)",
      roadCasing: "oklch(15% 0.014 145)",
      road: "oklch(72% 0.018 88)",
      railwayCasing: "oklch(11% 0.01 75)",
      railway: "oklch(62% 0.012 75)",
      path: "oklch(64% 0.02 88)",
      building: "oklch(43% 0.034 68)",
      buildingEdge: "oklch(76% 0.018 88)",
      player: "oklch(73% 0.105 58)",
      playerOutline: "oklch(94% 0.014 88)",
      viewCone: "oklch(76% 0.07 62 / 24%)",
      waypoint: "oklch(75% 0.13 68)",
      waypointOutline: "oklch(13% 0.018 145)",
    } satisfies MinimapPalette),
  }),
});

export const DEFAULT_MINIMAP_PREFERENCES: Readonly<MinimapPreferences> =
  Object.freeze({
    schemaVersion: 1,
    visible: true,
  });

export function normalizeMinimapPreferences(value: unknown): MinimapPreferences {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return { ...DEFAULT_MINIMAP_PREFERENCES };
  }
  const record = value as Record<string, unknown>;
  if (record.schemaVersion !== undefined && record.schemaVersion !== 1) {
    return { ...DEFAULT_MINIMAP_PREFERENCES };
  }
  return {
    schemaVersion: 1,
    visible: typeof record.visible === "boolean"
      ? record.visible
      : DEFAULT_MINIMAP_PREFERENCES.visible,
  };
}

export function parseMinimapPreferences(serialized: string): MinimapPreferences {
  try {
    return normalizeMinimapPreferences(JSON.parse(serialized));
  } catch {
    return { ...DEFAULT_MINIMAP_PREFERENCES };
  }
}

export function loadMinimapPreferences(
  storage: Pick<Storage, "getItem">,
): MinimapPreferences {
  try {
    const stored = storage.getItem(MINIMAP_PREFERENCES_STORAGE_KEY);
    return stored === null
      ? { ...DEFAULT_MINIMAP_PREFERENCES }
      : parseMinimapPreferences(stored);
  } catch {
    return { ...DEFAULT_MINIMAP_PREFERENCES };
  }
}

export function persistMinimapPreferences(
  storage: Pick<Storage, "setItem">,
  preferences: Readonly<MinimapPreferences>,
): boolean {
  try {
    storage.setItem(
      MINIMAP_PREFERENCES_STORAGE_KEY,
      JSON.stringify(normalizeMinimapPreferences(preferences)),
    );
    return true;
  } catch {
    return false;
  }
}
